import { useState, type FormEvent, type ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, ShieldCheck } from 'lucide-react'
import type { CustomerProfileInput, CustomerProfileResponse } from '@seaa/shared/contracts'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { ApiError, apiErrorMessage } from '../../lib/api'
import { useI18n } from '../../lib/i18n'
import { customerProfileKey, isCustomerIdentityError, saveCustomerProfile } from './customer-profile'

interface CustomerProfileFormProps {
  account: CustomerProfileResponse
  submitLabel?: string
  children?: ReactNode
  onSaved?: (account: CustomerProfileResponse, form: FormData) => void | Promise<void>
  onIdentityError?: () => void
}

export function CustomerProfileForm({ account, submitLabel, children, onSaved, onIdentityError }: CustomerProfileFormProps) {
  const { locale, l } = useI18n()
  const queryClient = useQueryClient()
  const [saved, setSaved] = useState(false)
  const save = useMutation({
    mutationFn: async ({ input, form }: { input: CustomerProfileInput; form: FormData }) => {
      const result = await saveCustomerProfile(account.user.id, input)
      if (result.user.id !== account.user.id) throw new ApiError(409, 'customer_session_changed', 'Please verify your account again.')
      if (!result.profile) throw new ApiError(409, 'customer_profile_required', 'Please complete your profile.')
      queryClient.setQueryData(customerProfileKey(result.user.id), result)
      await onSaved?.(result, form)
      setSaved(true)
    },
    onError(error) {
      if (isCustomerIdentityError(error)) {
        queryClient.removeQueries({ queryKey: ['customer-profile'] })
        queryClient.removeQueries({ queryKey: ['customer-account'] })
        onIdentityError?.()
      }
    },
  })
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (save.isPending) return
    const form = new FormData(event.currentTarget)
    const input: CustomerProfileInput = {
      firstName: String(form.get('firstName') ?? '').trim(),
      lastName: String(form.get('lastName') ?? '').trim(),
      email: String(form.get('email') ?? '').trim(), locale,
    }
    save.mutate({ input, form })
  }
  return <form onSubmit={submit} onChange={() => setSaved(false)} className="min-w-0" data-testid="customer-profile-form">
    <fieldset disabled={save.isPending} className="min-w-0">
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-available/25 bg-available/5 p-4 text-sm" data-testid="verified-customer-phone">
      <ShieldCheck className="shrink-0 text-available" size={20} />
      <strong>{l('Mobilnummer bestätigt','Mobile number verified','رقم الجوال موثّق','Mobiel nummer geverifieerd')}</strong>
      <span className="font-medium" dir="ltr">{account.user.phoneNumber}</span>
    </div>
    <div className="mt-5 grid min-w-0 gap-4 sm:grid-cols-2">
      <label className="min-w-0 text-sm font-semibold">{l('Vorname','First name','الاسم الأول','Voornaam')}<Input name="firstName" autoComplete="given-name" dir="auto" className="mt-2" defaultValue={account.profile?.firstName ?? ''} maxLength={80} required /></label>
      <label className="min-w-0 text-sm font-semibold">{l('Nachname','Last name','اسم العائلة','Achternaam')}<Input name="lastName" autoComplete="family-name" dir="auto" className="mt-2" defaultValue={account.profile?.lastName ?? ''} maxLength={80} required /></label>
      <label className="min-w-0 text-sm font-semibold sm:col-span-2">{l('E-Mail','Email','البريد الإلكتروني','E-mail')}<Input name="email" type="email" autoComplete="email" inputMode="email" dir="ltr" className="mt-2" defaultValue={account.profile?.email ?? ''} maxLength={254} required /></label>
    </div>
    <p className="mt-4 text-sm leading-6 text-muted">{l(
      'Diese Angaben werden in deinem Seaa-Profil gespeichert und bei künftigen Buchungen vorausgefüllt. Deine bestätigte Mobilnummer bleibt mit deinem Konto verknüpft.',
      'These details are saved to your Seaa profile and prefilled for future bookings. Your verified mobile number stays linked to your account.',
      'نحفظ هذه البيانات في ملفك لدى سِعَة لتعبئتها تلقائيًا في حجوزاتك القادمة. يظل رقم جوالك الموثّق مرتبطًا بحسابك.',
      'Deze gegevens worden in je Seaa-profiel opgeslagen en bij volgende boekingen ingevuld. Je geverifieerde mobiele nummer blijft aan je account gekoppeld.',
    )}</p>
    {children}
    {save.isError && <p role="alert" className="mt-4 text-sm text-danger">{apiErrorMessage(save.error, locale)}</p>}
    {saved && !onSaved && <p role="status" className="mt-4 flex items-center gap-2 text-sm text-available"><Check size={17} />{l('Profil gespeichert','Profile saved','حُفظ ملفك','Profiel opgeslagen')}</p>}
    <Button type="submit" size="lg" className="mt-6 w-full sm:w-auto" disabled={save.isPending}>
      {save.isPending ? l('Wird gespeichert…','Saving…','جارٍ الحفظ…','Opslaan…') : submitLabel ?? l('Profil speichern','Save profile','حفظ بياناتي','Profiel opslaan')}
    </Button>
    </fieldset>
  </form>
}
